import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface CheckoutOptions {
  product_id: string;
  pricing_type: 'price_1_2_year' | 'price_3_5_year' | 'price_lifetime';
  amount: number;
  provider: 'nowpayments' | 'stripe';
}

interface CheckoutResult {
  success: boolean;
  checkout_url?: string;
  invoice_url?: string;
  order_id?: string;
  error?: string;
}

export function useCheckout() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createCheckout = useCallback(async (options: CheckoutOptions): Promise<CheckoutResult> => {
    setLoading(true);
    setError(null);

    try {
      // Refresh session first to ensure token is not stale
      const { data: { session } } = await supabase.auth.refreshSession();
      const activeSession = session ?? (await supabase.auth.getSession()).data.session;

      if (!activeSession?.access_token) {
        setError('Please sign in to continue');
        return { success: false, error: 'Please sign in to continue' };
      }

      const functionName = options.provider === 'stripe' 
        ? 'stripe-create-checkout' 
        : 'nowpayments-create-invoice';

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/${functionName}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${activeSession.access_token}`,
          },
          body: JSON.stringify({
            product_id: options.product_id,
            pricing_type: options.pricing_type,
            amount: options.amount,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        // Surface the most useful error: detail > error > status
        const errorMsg = data.detail
          ? `${data.error || 'Checkout failed'}: ${typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail)}`
          : data.error || `Request failed (HTTP ${response.status})`;
        setError(errorMsg);
        console.error('[Checkout error]', response.status, data);
        return { success: false, error: errorMsg };
      }

      return {
        success: true,
        checkout_url: data.checkout_url,
        invoice_url: data.invoice_url,
        order_id: data.order_id,
      };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Network error';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    createCheckout,
    loading,
    error,
  };
}